import type { FC, ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import { useLocation, Routes, Route, Navigate, useParams } from "react-router";
import { DynamicForm } from "../../dynamicForm";
import { Drawer } from "../../shared/Drawer";
import {
  dpiaByIdQueryOptions,
  dpiaFormQueryOptions,
  updateDpiaAnswersMutationOptions,
  dpiaRiskAssessmentMatrixByIdQueryOptions,
  dpiaRiskAssessmentsQueryOptions,
  useUpdateDpiaRiskAssessmentById,
} from "../../api/dpia";
import type { DpiaById } from "../../api/dpia.types";
import {
  SIDEBAR_WIDTH,
  useElementHeight,
  type MultiStepFormProps,
} from "../../dynamicForm/multiStepForm/MultiStepForm";
import { mapSectionsToSectionGroupsWithSections } from "../../dynamicForm/multiStepForm/mapSectionsToSectionGroupsWithSections";
import { SectionGroupsNavigation, SectionNavigation } from "../../dynamicForm/multiStepForm/SectionNavigation";
import { Sections } from "../../dynamicForm/multiStepForm/Sections";
import { customScrollbar } from "../../shared/customScrollbar";
import { PageTitle } from "../../shared/Page";
import { RiskAssessment } from "./RiskAssessment";
import { DpiaRightDrawer } from "./DpiaRightDrawer";
import { DpiaDrawerHeader } from "./DpiaDrawerHeader";
import { DynamicGrid } from "../../dynamicGrid";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { useMutation, useQuery } from "@tanstack/react-query";

export const DpiaMultiStepForm = () => {
  const { paths } = usePrivacyNavigate();
  const dpiaId = DynamicGrid.useSelectedRowId();
  const dpiaQuery = useQuery(dpiaByIdQueryOptions(dpiaId));
  const formQuery = useQuery(dpiaFormQueryOptions());
  const updateDpiaAnswers = useMutation(updateDpiaAnswersMutationOptions());
  const updateRiskAssessmentMutation = useUpdateDpiaRiskAssessmentById();
  /** Prefetching data for RiskAssessment.tsx. This is done to remove flickering when loading initial data for RiskAssessment */
  useQuery(dpiaRiskAssessmentsQueryOptions(dpiaId));
  useQuery(dpiaRiskAssessmentMatrixByIdQueryOptions(dpiaId));

  return (
    <DynamicForm.MultiStepFormDialog>
      {(() => {
        if (!dpiaQuery.data) return <Drawer.Loading />;

        const dpia = dpiaQuery.data;

        if (!dpia.preAssessment.isCompleted) return <Navigate to={paths.dpiaPreAssessmentById(dpia.id)} replace />;

        return (
          <DynamicForm.Provider
            onSave={answer => updateDpiaAnswers.mutateAsync({ id: dpia.id, answers: [answer] })}
            answers={dpia.answers}
            isSaveError={updateDpiaAnswers.isError || updateRiskAssessmentMutation.isError}
            isSaving={updateDpiaAnswers.isPending || updateRiskAssessmentMutation.isPending}
            isReadOnly={!dpia.canEdit}
          >
            {formQuery.data ? (
              <MultiStepForm
                header={<DpiaDrawerHeader dpia={dpia} activeTab="form" />}
                form={formQuery.data}
                dpia={dpia}
                RiskAssessment={
                  <RiskAssessment
                    key={dpia.canEdit ? "canEdit" : "readonly"}
                    dpiaId={dpia.id}
                    updateRiskAssessmentMutation={updateRiskAssessmentMutation}
                    isReadOnly={!dpia.canEdit}
                  />
                }
              />
            ) : (
              <>
                <DpiaDrawerHeader dpia={dpia} activeTab="form" />
                <Drawer.Loading />
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicForm.MultiStepFormDialog>
  );
};

const MultiStepForm: FC<MultiStepFormProps & { RiskAssessment: ReactNode; dpia: DpiaById }> = ({
  form,
  header,
  RiskAssessment,
  dpia,
}) => {
  const { sections } = form;
  const location = useLocation();
  const [firstSection] = sections;

  return (
    <Routes>
      <Route
        path=":sectionId"
        element={<MultiStepFormInner form={form} header={header} RiskAssessment={RiskAssessment} dpia={dpia} />}
      />
      {/* Replace with first section if no match*/}
      <Route path="*" element={<Navigate to={`${firstSection.id}${location.search}`} replace />} />
    </Routes>
  );
};

const MultiStepFormInner: FC<MultiStepFormProps & { RiskAssessment: ReactNode; dpia: DpiaById }> = ({
  form,
  header,
  RiskAssessment,
  dpia,
}) => {
  const [headerRef, headerHeight] = useElementHeight();
  const { sections } = form;
  const { sectionId } = useParams();

  const currentSection = sections.find(step => step.id === sectionId);

  /** If the URL-param do not match any section redirect to first section **/
  if (!currentSection) {
    const [firstSection] = sections;
    return <Navigate to={`${firstSection.id}`} replace />;
  }

  return (
    <>
      <Box ref={headerRef} sx={{ zIndex: 1, position: "sticky", top: 0 }}>
        {header}
      </Box>
      <Stack direction="row" sx={{ width: "100%" }}>
        <MuiDrawer
          variant="permanent"
          sx={{
            zIndex: 0,
            width: SIDEBAR_WIDTH,
            [`& .${drawerClasses.paper}`]: {
              bgcolor: theme => theme.palette.grey["100"],
              width: SIDEBAR_WIDTH,
              overflow: "hidden",
              pt: `${headerHeight}px`,
            },
          }}
        >
          <Box sx={{ overflow: "auto", ...customScrollbar }}>
            {form.sectionGroups ? (
              <SectionGroupsNavigation
                sectionGroupsWithSections={mapSectionsToSectionGroupsWithSections(sections, form.sectionGroups)}
                currentSectionId={currentSection.id}
              />
            ) : (
              <SectionNavigation sections={sections} currentSectionId={currentSection.id} />
            )}
          </Box>
        </MuiDrawer>
        <Stack sx={{ maxWidth: 1080, mx: "auto", alignItems: "center", flex: "1", px: 3, my: 6 }}>
          <Box sx={{ width: "100%" }}>
            <PageTitle
              wrapperSx={{ maxWidth: "700px", mb: 2 }}
              title={currentSection.name}
              subTitle={currentSection.description}
              wrap
            />
          </Box>

          {currentSection.questionCards.length === 0 && RiskAssessment}

          <Sections sections={sections} currentSection={currentSection} />
        </Stack>
        <DpiaRightDrawer headerHeight={headerHeight} dpia={dpia} />
      </Stack>
    </>
  );
};
